import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ConfirmDialog from './ConfirmDialog'
import { SplitCatSelect } from './SplitCatSelect'
import {
  type Opt, type SplitRow, type TplItem,
  brl, inputCls,
} from './finance-shared'
import {
  FiPlus, FiEdit2, FiTrash2, FiLoader, FiCheck, FiX, FiDownload,
} from 'react-icons/fi'

// Caixa de rateio DA CATEGORIA — aparece no modal de lançamento quando a
// categoria escolhida tem opens_split=1 (JVN SERVIÇOS, faturas de cartão).
// Desde 2026-10-16 aparece também em QUALQUER despesa com o rateio aberto,
// mas aí só quando a categoria já tem modelo (ver `ocultarSeVazio`).
//
// Por que existe: o quadro "Rateio por categoria" abre sozinho nessas
// categorias desde a migração 037, mas abre VAZIO. Todo mês o operador
// redigita a mesma lista de partes. Em 2026-07-30 alguém rateou o JVN de
// julho em 7 partes que fecham exato — trabalho que hoje morre no lançamento.
//
// A caixa cuida SÓ do modelo: a lista de partes que se repete, salva na
// categoria (category_split_template, migração 038), editável item a item, com
// o botão Aplicar jogando tudo no quadro "Rateio por categoria" logo abaixo.
//
// Houve também uma seção "Lançamentos desta categoria", listando os pagamentos
// anteriores com o rateio de cada um. Foi REMOVIDA em 2026-07-30 a pedido: na
// prática ela competia com o rateio do lançamento que se está editando, que é
// o que interessa na hora. Para mexer no rateio de um pagamento antigo, abre-se
// aquele lançamento — o quadro de baixo já carrega as partes dele.
// As rotas POST/PATCH/DELETE por item em transactions.cjs continuam válidas e
// testadas; hoje nenhuma tela as chama.
//
// REGRA IMPORTANTE: Aplicar NÃO chama API nenhuma — só devolve linhas pelo
// onAplicar. A gravação do rateio do lançamento continua num ponto único, o
// salvarSplits do TxnModal (PUT /api/transactions/:id/splits).

const n2 = (n: number) => Math.round(n * 100) / 100
const paraCampo = (n: number) => n.toFixed(2).replace('.', ',')
const doCampo = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0

type Rascunho = { target_category_id: string; modo: 'percent' | 'valor' | 'vazio'; valor: string; description: string }
const RASCUNHO_VAZIO: Rascunho = { target_category_id: '', modo: 'vazio', valor: '', description: '' }

const rascunhoDe = (t: TplItem): Rascunho => ({
  target_category_id: t.target_category_id ? String(t.target_category_id) : '',
  modo: t.percent != null ? 'percent' : t.amount != null ? 'valor' : 'vazio',
  valor: t.percent != null ? String(Number(t.percent)).replace('.', ',')
    : t.amount != null ? paraCampo(Number(t.amount)) : '',
  description: t.description || '',
})

const corpoDe = (r: Rascunho) => ({
  target_category_id: r.target_category_id ? Number(r.target_category_id) : null,
  description: r.description.trim() || null,
  percent: r.modo === 'percent' ? doCampo(r.valor) : null,
  amount: r.modo === 'valor' ? doCampo(r.valor) : null,
})

export function RateioCategoriaBox({
  categoryId, categoryName, cats, totalLancamento,
  temSplitsAtuais, modeloVersao = 0, onAplicar, onModeloCount, ocultarSeVazio = false,
}: {
  categoryId: number
  categoryName: string
  cats: Opt[]
  totalLancamento: number
  temSplitsAtuais: boolean
  // Contador que o TxnModal incrementa ao gravar "Salvar como modelo" — é o
  // sinal para esta caixa recarregar o modelo que acabou de mudar por fora.
  modeloVersao?: number
  onAplicar: (rows: SplitRow[]) => void
  // Quantas partes o modelo tem agora. O TxnModal usa para decidir se o
  // "Salvar como modelo" precisa confirmar (ele SUBSTITUI o modelo inteiro).
  onModeloCount?: (n: number) => void
  // Categoria SEM opens_split (2026-10-16): a caixa só aparece se já houver
  // modelo. Um rateio avulso (ex.: marketing lançado junto com o pagamento das
  // líderes) não precisa de um quadro vazio convidando a cadastrar modelo — o
  // modelo nasce pelo "Salvar como modelo" do quadro de rateio. A caixa
  // continua MONTADA (carrega e reporta onModeloCount); só não desenha.
  ocultarSeVazio?: boolean
}) {
  const { authenticatedFetch } = useAuth()

  // ── seção A: modelo ──
  const [tpl, setTpl] = useState<TplItem[] | null>(null)
  const [tplErr, setTplErr] = useState<string | null>(null)
  const [tplBusy, setTplBusy] = useState(false)
  const [editandoId, setEditandoId] = useState<number | null>(null)
  const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO)
  const [novoAberto, setNovoAberto] = useState(false)
  // Nome de rede para o combobox do editor, igual ao que o TxnModal faz nas
  // partes do lançamento: se o lookup na lista não casar, mostra o nome que a
  // API já mandou em vez de deixar o campo em branco.
  const [rascunhoNome, setRascunhoNome] = useState<string | undefined>(undefined)

  const [confirmar, setConfirmar] = useState<{ rows: SplitRow[] } | null>(null)

  // Contador de requisição: trocar de categoria rápido (A→B→A) pode fazer as
  // respostas chegarem fora de ordem, e a última a chegar venceria. Só a
  // resposta da requisição mais recente é aceita.
  const seqRef = useRef(0)

  const loadTpl = useCallback(async () => {
    const seq = ++seqRef.current
    setTplBusy(true); setTplErr(null)
    try {
      const r = await authenticatedFetch(`/api/categories/${categoryId}/split-template`)
      const d = await r.json()
      if (seq !== seqRef.current) return
      if (d.success) setTpl(d.items)
      else setTplErr(d.error || 'Falha ao carregar o modelo')
    } catch { if (seq === seqRef.current) setTplErr('Erro de conexão') }
    finally { if (seq === seqRef.current) setTplBusy(false) }
  }, [authenticatedFetch, categoryId, modeloVersao])

  // Recarrega ao montar, ao trocar de categoria e quando o TxnModal grava
  // "Salvar como modelo" (que incrementa modeloVersao).
  useEffect(() => { loadTpl() }, [loadTpl])

  // Espelha a contagem para fora sempre que o modelo muda.
  useEffect(() => { onModeloCount?.(tpl?.length ?? 0) }, [tpl, onModeloCount])

  // Trocar a categoria PRECISA zerar `tpl`, não só fechar a edição. O
  // componente não remonta (mesma posição na árvore, sem key), então sem isto
  // a caixa exibe o nome da categoria NOVA com as partes da ANTIGA enquanto o
  // GET não volta — e "Aplicar" nessa janela joga o modelo errado no rateio.
  useEffect(() => { setTpl(null); setEditandoId(null); setNovoAberto(false) }, [categoryId])

  // ── modelo: gravações ──
  const gravarTpl = async (metodo: 'POST' | 'PUT', url: string, corpo: unknown) => {
    setTplBusy(true); setTplErr(null)
    try {
      const r = await authenticatedFetch(url, { method: metodo, body: JSON.stringify(corpo) })
      const d = await r.json()
      if (!d.success) { setTplErr(d.error || 'Falha ao salvar'); return false }
      await loadTpl()
      return true
    } catch { setTplErr('Erro de conexão'); return false }
    finally { setTplBusy(false) }
  }

  const salvarNovo = async () => {
    if (await gravarTpl('POST', `/api/categories/${categoryId}/split-template`, corpoDe(rascunho))) {
      setNovoAberto(false); setRascunho(RASCUNHO_VAZIO)
    }
  }
  const salvarEdicao = async (id: number) => {
    if (await gravarTpl('PUT', `/api/categories/${categoryId}/split-template/${id}`, corpoDe(rascunho))) {
      setEditandoId(null); setRascunho(RASCUNHO_VAZIO)
    }
  }
  const removerTpl = async (id: number) => {
    setTplBusy(true); setTplErr(null)
    try {
      const r = await authenticatedFetch(`/api/categories/${categoryId}/split-template/${id}`, { method: 'DELETE' })
      const d = await r.json()
      if (!d.success) setTplErr(d.error || 'Falha ao remover')
      else await loadTpl()
    } catch { setTplErr('Erro de conexão') }
    finally { setTplBusy(false) }
  }

  // ── Aplicar: converte o modelo em linhas do quadro de rateio ──
  //
  // Percentual incide sobre o TOTAL do lançamento; valor fixo entra literal;
  // parte sem nenhum dos dois vem com o valor em branco. O resíduo de
  // arredondamento (ex.: 3 partes de 33,33% num total ímpar) vai para a MAIOR
  // linha percentual — e, em empate, para a de menor position, para o
  // resultado ser o mesmo toda vez.
  const montarDoModelo = (): SplitRow[] => {
    const itens = tpl || []
    const rows: SplitRow[] = itens.map(t => ({
      category_id: t.target_category_id ? String(t.target_category_id) : '',
      amount: t.percent != null ? paraCampo(n2(totalLancamento * Number(t.percent) / 100))
        : t.amount != null ? paraCampo(Number(t.amount)) : '',
      description: t.description || '',
    }))
    const idxPct = itens.map((t, i) => (t.percent != null ? i : -1)).filter(i => i >= 0)
    const somaPct = itens.reduce((a, t) => a + (t.percent != null ? Number(t.percent) : 0), 0)
    if (idxPct.length > 1 && Math.abs(somaPct - 100) < 0.0001) {
      const alvo = n2(totalLancamento - itens.reduce((a, t) => a + (t.amount != null ? Number(t.amount) : 0), 0))
      const distribuido = n2(idxPct.reduce((a, i) => a + doCampo(rows[i].amount), 0))
      const residuo = n2(alvo - distribuido)
      if (residuo !== 0) {
        let maior = idxPct[0]
        for (const i of idxPct) if (doCampo(rows[i].amount) > doCampo(rows[maior].amount)) maior = i
        rows[maior].amount = paraCampo(n2(doCampo(rows[maior].amount) + residuo))
      }
    }
    return rows
  }

  const pedirAplicar = (rows: SplitRow[]) => {
    if (temSplitsAtuais) setConfirmar({ rows })
    else onAplicar(rows)
  }

  const temPercentual = (tpl || []).some(t => t.percent != null)
  const semTotal = temPercentual && !(totalLancamento > 0)
  const somaPercentual = (tpl || []).reduce((a, t) => a + (t.percent != null ? Number(t.percent) : 0), 0)

  const btnIcone = 'w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-40'
  const btnLixo = 'w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-40'
  // PLANOS (21/10/2026): chips na altura dos campos (h-9, 14px) — o TxnModal
  // virou seções de papel e as ações internas seguem a mesma métrica.
  const btnChip = 'neu-convex inline-flex items-center gap-1.5 h-9 text-sm rounded-lg border border-border px-3 hover:bg-secondary disabled:opacity-40'

  // Rótulo curto acima de cada campo — é o idioma de formulário do resto do
  // app (TxnModal usa `label` + `inputCls` em grid). A versão anterior punha
  // combobox + select + valor numa linha e descrição + 2 botões de 28px noutra:
  // as bordas direitas não se encontravam e os botões ficavam metade da altura
  // dos campos. Daí "desproporcional e desalinhado".
  const rot = 'block text-[12.5px] font-medium text-muted-foreground mb-1'

  if (ocultarSeVazio && !novoAberto && !(tpl && tpl.length)) return null

  const editorLinha = (titulo: string, onOk: () => void, onCancel: () => void) => (
    <div className="neu-convex mb-2 rounded-lg border border-primary/40 bg-secondary/30 p-3 space-y-3">
      <p className="text-sm font-semibold text-foreground">{titulo}</p>

      <div>
        <label className={rot}>Categoria de destino</label>
        <SplitCatSelect value={rascunho.target_category_id} options={cats}
          hint={rascunho.description || undefined} fallbackName={rascunhoNome}
          onChange={id => setRascunho(r => ({ ...r, target_category_id: id }))} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={rot}>Como calcula</label>
          <select value={rascunho.modo}
            onChange={e => setRascunho(r => ({ ...r, modo: e.target.value as Rascunho['modo'], valor: '' }))}
            className={`${inputCls} h-9`}>
            <option value="vazio">Deixar em branco</option>
            <option value="percent">Percentual</option>
            <option value="valor">Valor fixo</option>
          </select>
        </div>
        <div>
          <label className={rot}>
            {rascunho.modo === 'percent' ? 'Percentual (%)' : rascunho.modo === 'valor' ? 'Valor (R$)' : 'Valor'}
          </label>
          <input value={rascunho.valor} inputMode="decimal"
            placeholder={rascunho.modo === 'vazio' ? '—' : '0,00'}
            disabled={rascunho.modo === 'vazio'}
            onChange={e => setRascunho(r => ({ ...r, valor: e.target.value }))}
            className={`${inputCls} h-9 text-right disabled:opacity-40`} />
        </div>
      </div>

      <div>
        <label className={rot}>Descrição <span className="font-normal">(opcional)</span></label>
        <input value={rascunho.description} placeholder="ex.: Contrato A"
          onChange={e => setRascunho(r => ({ ...r, description: e.target.value }))}
          className={`${inputCls} h-9`} />
      </div>

      {/* Botões com texto, no rodapé à direita — mesma gramática do rodapé do
          drawer (neu-tab para sair, neu-relief primário para confirmar). */}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} disabled={tplBusy}
          className="neu-tab inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm text-muted-foreground hover:bg-secondary disabled:opacity-40">
          <FiX className="w-3.5 h-3.5" /> Cancelar
        </button>
        <button type="button" onClick={onOk} disabled={tplBusy}
          className="neu-relief inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
          {tplBusy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />} Salvar parte
        </button>
      </div>
    </div>
  )

  return (
    // PLANOS (21/10/2026): mora dentro da seção "Rateio por categoria" do
    // TxnModal (papel). Vira sub-caixa leve: só borda, sem relevo neu-convex.
    <div className="rounded-lg border border-border p-3.5">
      {/* Título/texto em cima, ações numa fileira própria. O drawer é max-w-lg
          (512px) — botão ao lado de parágrafo não cabe. */}
      <div className="mb-3">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          Modelo de rateio da categoria
          {tplBusy && <FiLoader className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </span>
        <p className="text-[13px] text-muted-foreground mt-1">
          As partes que se repetem em “{categoryName}”. Ficam salvas na categoria — no próximo
          lançamento é só aplicar.
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <button type="button" className={btnChip} disabled={tplBusy || novoAberto}
            onClick={() => { setRascunho(RASCUNHO_VAZIO); setRascunhoNome(undefined); setNovoAberto(true); setEditandoId(null) }}>
            <FiPlus className="w-4 h-4" /> Adicionar parte
          </button>
          <button type="button" className={btnChip}
            disabled={tplBusy || !tpl?.length || semTotal}
            title={semTotal ? 'Informe o valor do lançamento primeiro — o modelo tem partes em percentual.' : 'Preenche o rateio abaixo com as partes do modelo'}
            onClick={() => pedirAplicar(montarDoModelo())}>
            <FiDownload className="w-4 h-4" /> Aplicar ao rateio
          </button>
        </div>
      </div>

      {tplErr && <div className="text-[13px] text-red-500 mb-2">{tplErr}</div>}

      {novoAberto && editorLinha('Nova parte do modelo', salvarNovo,
        () => { setNovoAberto(false); setRascunho(RASCUNHO_VAZIO) })}

      {tpl !== null && tpl.length === 0 && !novoAberto && (
        <p className="rounded-md border border-dashed border-border px-3 py-2.5 text-[13px] text-muted-foreground">
          Nenhuma parte no modelo ainda. Adicione as que se repetem todo mês — ou rateie este
          lançamento e clique em “Salvar como modelo”.
        </p>
      )}

      {/* Lista das partes salvas. Coluna de valor com largura FIXA para os
          números alinharem uns sob os outros, e as ações sempre visíveis (o
          opacity-0 no hover deixava a linha oca e movia o layout ao passar o
          mouse). */}
      {(tpl || []).length > 0 && (
        <div className="divide-y divide-border/60">
          {(tpl || []).map(t => (
            editandoId === t.id
              ? <div key={t.id} className="py-1">
                  {editorLinha('Editando parte do modelo', () => salvarEdicao(t.id),
                    () => { setEditandoId(null); setRascunho(RASCUNHO_VAZIO) })}
                </div>
              : (
                <div key={t.id} className="group flex items-center gap-2 rounded-md px-2 py-2 hover:bg-secondary/60 transition-colors">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-foreground">
                      {t.target_category_name || <span className="text-muted-foreground italic">(sem categoria)</span>}
                    </span>
                    {t.description && (
                      <span className="block truncate text-xs text-muted-foreground">{t.description}</span>
                    )}
                  </span>
                  <span className="w-24 shrink-0 text-right tabular-nums text-sm text-foreground">
                    {t.percent != null ? `${Number(t.percent).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`
                      : t.amount != null ? brl(Number(t.amount))
                      : <span className="text-xs italic text-muted-foreground">em branco</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-0.5">
                    <button type="button" title="Editar esta parte do modelo" disabled={tplBusy}
                      onClick={() => { setRascunho(rascunhoDe(t)); setRascunhoNome(t.target_category_name || undefined); setEditandoId(t.id); setNovoAberto(false) }}
                      className={btnIcone}><FiEdit2 className="w-3.5 h-3.5" /></button>
                    <button type="button" title="Remover do modelo" disabled={tplBusy}
                      onClick={() => removerTpl(t.id)}
                      className={btnLixo}><FiTrash2 className="w-3.5 h-3.5" /></button>
                  </span>
                </div>
              )
          ))}
        </div>
      )}

      {temPercentual && (tpl || []).length > 0 && (
        <p className={`mt-2 text-[13px] ${Math.abs(somaPercentual - 100) < 0.0001 ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}>
          {Math.abs(somaPercentual - 100) < 0.0001
            ? 'Os percentuais somam 100%.'
            : `Os percentuais somam ${somaPercentual.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}% — o restante fica sem classificação ao aplicar.`}
        </p>
      )}

      {/* portal + zIndex: o diálogo precisa aparecer POR CIMA do drawer
          (z-[100000]). closeOnEsc: sem isso o Esc chega no SideModal e fecha o
          lançamento inteiro, perdendo tudo que foi digitado. */}
      <ConfirmDialog
        isOpen={!!confirmar}
        portal zIndex={100050} closeOnEsc
        type="warning"
        title="Substituir o rateio atual"
        message="O quadro de rateio abaixo já tem partes preenchidas. Elas serão substituídas pelas do modelo."
        confirmText="Substituir"
        onConfirm={() => { if (confirmar) { onAplicar(confirmar.rows); setConfirmar(null) } }}
        onCancel={() => setConfirmar(null)}
      />
    </div>
  )
}
